'use client';

import { trackedEntity } from '@cineamo/pulse';
import { usePulseTracker } from '@cineamo/pulse/react';
import { getImageProps } from 'next/image';
import type React from 'react';
import { preload } from 'react-dom';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { TrailerAndPosterCompositeImage } from '@cineamo/composables/src/TrailerAndPosterCompositeImage';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const ContentDetailImage: React.FC<{
    content: CachedContentData;
    className?: string;
    /** Bare 72x108 poster with the trailer button on top, see `TrailerAndPosterCompositeImage`. */
    compact?: boolean;
    /** Eager-load the images; pass `false` for the instance hidden below `lg`. */
    priority?: boolean;
    /** Overrides `priority` for the poster overlay of the non-compact layout. */
    posterPriority?: boolean;
    /** Preload the hero image for this media query only, for an instance that is hidden at other breakpoints. */
    preloadMedia?: string;
}> = ({ content, className, compact, priority, posterPriority, preloadMedia }) => {
    const { track: pulseTrack } = usePulseTracker();
    // The site's own cinema decides whose youth-protection regime applies (CIN-14724).
    const { cinema, websiteConfiguration } = useDomainConfiguration();

    const heroImage = compact ? (content.posterImage ?? content.backdropImage) : content.backdropImage;
    if (preloadMedia && heroImage) {
        const heroSizes = compact ? '72px' : '(max-width: 1024px) 100vw, 1024px';
        const { props } = getImageProps({
            src: heroImage,
            alt: '',
            width: compact ? 300 : 1000,
            height: compact ? 450 : 1000,
            sizes: heroSizes
        });
        // React 19 forwards `media` to the preload link; @types/react-dom 18 does not declare it yet,
        // so the options go through a variable to skip the excess-property check.
        const preloadOptions = {
            as: 'image' as const,
            imageSrcSet: props.srcSet,
            imageSizes: heroSizes,
            fetchPriority: 'high' as const,
            media: preloadMedia
        };
        preload(props.src, preloadOptions);
    }

    return (
        <TrailerAndPosterCompositeImage
            title={content.title}
            backdropImage={content.backdropImage}
            backdropBlurDataImage={content.backdropBlurDataImage}
            posterImage={content.posterImage}
            posterBlurDataImage={content.posterBlurDataImage}
            trailerUrl={content.trailerUrl}
            gate={
                websiteConfiguration?.isTrailerAgeGateDisabled
                    ? undefined
                    : { rating: content.trailerAgeRating, countryCode: cinema?.countryCode }
            }
            showPoster={content.showPoster}
            hideTrailer={!content.trailerUrl}
            className={className}
            compact={compact}
            sizes={compact ? '72px' : '(max-width: 1024px) 100vw, 1024px'}
            priority={priority}
            posterPriority={posterPriority}
            trackingData={{
                movieId: content.movieId ?? ''
            }}
            onTrailerPlay={() => {
                pulseTrack({
                    ...trackedEntity({ movieId: content.movieId, contentId: content.id }),
                    event_type: 'trailer_play'
                });
            }}
            onTrailerFinished={() => {
                pulseTrack({
                    ...trackedEntity({ movieId: content.movieId, contentId: content.id }),
                    event_type: 'trailer_play_finished'
                });
            }}
        />
    );
};

export default ContentDetailImage;
